import React, { FC, useMemo, useState } from 'react';
import {
  AppFooter_v2 as AppFooter,
  Button_v2 as Button,
  OnBoardingScreenOptions,
  OnBoardingScreensType,
  IconArrowRight,
  OnBoardingScreenOptionWithScaleType,
  CSSVarStyles,
  OnboardingVariant,
  OnBoardingThemeV2Type,
} from '@mindly/ui-components';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { FunnelTypeEnum, useAnalytics, useAuthFlow } from 'src/shared';
import { deepLink } from 'src/shared/utils/deepLink';
import { isPlatform, useIonRouter } from '@ionic/react';
import { createCustomTokenForAnonymousUser } from 'src/api/auth/auth';
import { changeUserData } from 'src/api/user';
import { parseAttributionParams } from 'src/utils/parseAttributionParams';
import { clearSavedFilters, clearSavedSort } from 'src/shared/utils/filters';
import { PrivateRoutes, PublicRoutes } from 'src/routes/navigation';
import { useHistory } from 'react-router';
import { loadUserThunk } from 'src/redux/user/thunk';
import { onBoardingFlowSelector } from 'src/redux/onBoardingFlow/selector';
import { SCREEN_OPTIONS_WITHOUT_NEXT_BUTTON } from './constants';

type OnBoardingFlowPageFooterProps = {
  currentScreenData: OnBoardingScreensType;
  onNextClick?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  buttonsStyles?: CSSVarStyles;
  skipButtonStyles?: CSSVarStyles;
  className?: string;
  onboardingTheme?: OnBoardingThemeV2Type;
  flowId: string;
  isSubGoalScreen?: boolean;
};

const OnBoardingFlowPageFooter: FC<OnBoardingFlowPageFooterProps> = ({
  currentScreenData,
  onNextClick,
  isDisabled,
  buttonsStyles,
  skipButtonStyles,
  className,
  isLoading,
  onboardingTheme,
  flowId,
  isSubGoalScreen = false,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const navigation = useIonRouter();
  const { t, i18n } = useTranslation('onboarding');
  const { trackPredefinedEvent } = useAnalytics();
  const { authUser } = useAuthFlow();
  const flowData = useSelector(onBoardingFlowSelector);

  const [isSkipLoading, setIsSkipLoading] = useState(false);

  const isShowSkipButton = useMemo(() => {
    return (
      currentScreenData.type === OnBoardingScreenOptions.START &&
      'showSkipButton' in currentScreenData &&
      currentScreenData.showSkipButton
    );
  }, [currentScreenData]);

  const parseUtmParams = () => {
    try {
      const utmParams = localStorage.getItem('utm_params');
      if (utmParams) {
        const parsedUtmParams = JSON.parse(utmParams);
        return parsedUtmParams;
      }
      return {};
    } catch (e) {}
  };

  const trackAnalyticsEvent = (event: 'FUNNEL_SKIP_CLICKED' | 'FUNNEL_START_CLICKED') => {
    if (!flowId) return;
    trackPredefinedEvent(event, {
      funnel_id: flowId,
      funnel_name: flowData?.name,
      funnel_type: flowData?.isDefault
        ? FunnelTypeEnum.DEFAULT
        : flowData?.isWebToWeb
          ? FunnelTypeEnum.WEB_TO_WEB
          : FunnelTypeEnum.WEB_TO_APP,
      screen_id: currentScreenData.id,
      screen_name: currentScreenData.name ?? '',
      locale: i18n.language,
    });
  };

  const onSkipClick = async () => {
    setIsSkipLoading(true);
    trackAnalyticsEvent('FUNNEL_SKIP_CLICKED');
    try {
      clearSavedFilters(i18n.language);
      clearSavedSort();
      if (!authUser?.uid) {
        return;
      }

      const attributionParams = parseAttributionParams();
      await changeUserData(
        {
          is_viewed_specialist_list: true,
          onboarding_key: flowData?.id ?? '',
          ...attributionParams,
        },
        authUser.uid ?? ''
      );
      if (!authUser?.email) {
        let token: string | null = null;
        try {
          const customToken = await createCustomTokenForAnonymousUser(authUser.uid);
          token = customToken?.data ?? null;
        } catch (error) {
          console.error(`FlowStartScreenPage handleSkipClicked: custom token error: ${(error as Error)?.message}`);
        }
        trackPredefinedEvent('Lead', {
          eventInstanceId: authUser.uid,
          userId: authUser.uid,
        });
        if (!flowData?.isWebToWeb && (isPlatform('android') || isPlatform('ios')) && !isPlatform('hybrid')) {
          const utmParams = parseUtmParams();
          deepLink({ ...utmParams, token, lang: i18n.language, skip_onboarding: true }, 'auth');
          return;
        }
        history.push(`${PublicRoutes.AUTH}?token=${token}&skip_onboarding=true`);
        return;
      }
      await dispatch(loadUserThunk(authUser.uid ?? ''));
      navigation.push(PrivateRoutes.PSYCHOLOGIST_LIST, 'forward', 'push');
    } catch (error) {
      console.error(error);
    } finally {
      setIsSkipLoading(false);
    }
  };

  const onStartClick = () => {
    trackAnalyticsEvent('FUNNEL_START_CLICKED');

    onNextClick?.();
  };

  const showNextButton = useMemo(() => {
    const { type } = currentScreenData;

    if (type === OnBoardingScreenOptions.SINGLE_SCALE_SELECT && 'options' in currentScreenData) {
      const hasFeedbackInfo = currentScreenData.options?.some((option) => {
        const opt = option as OnBoardingScreenOptionWithScaleType;
        return !!opt.feedbackTitle && !!opt.feedbackDescription;
      });

      return Boolean(hasFeedbackInfo);
    }

    return !SCREEN_OPTIONS_WITHOUT_NEXT_BUTTON.includes(type);
  }, [currentScreenData]);

  const buttonText = useMemo(() => {
    if (!currentScreenData) {
      return null;
    }

    if (isSubGoalScreen && 'subgoalButtonText' in currentScreenData && currentScreenData.subgoalButtonText) {
      return currentScreenData.subgoalButtonText;
    } else if (!isSubGoalScreen && 'buttonText' in currentScreenData && currentScreenData.buttonText) {
      return currentScreenData.buttonText;
    }
  }, [currentScreenData, isSubGoalScreen]);

  return (
    // @ts-ignore
    <AppFooter className={className} toolbarStyle={{ '--padding-bottom': '30px' }}>
      {showNextButton && (
        <Button
          onClick={currentScreenData.type === OnBoardingScreenOptions.START ? onStartClick : onNextClick}
          fullWidth
          style={buttonsStyles}
          size={'large'}
          disabled={isDisabled}
          isLoading={isLoading}
          variant={onboardingTheme?.template ?? OnboardingVariant.default}
        >
          {buttonText ?? t('onboarding.next_button')}
        </Button>
      )}
      {isShowSkipButton && (
        <Button
          onClick={onSkipClick}
          fullWidth
          style={skipButtonStyles}
          size={'default'}
          disabled={isDisabled}
          fill="clear"
          className="!mt-2"
          isLoading={isSkipLoading}
          variant={onboardingTheme?.template ?? OnboardingVariant.default}
        >
          <div className="flex justify-center items-center gap-2">
            {currentScreenData && 'skipButtonText' in currentScreenData && currentScreenData.skipButtonText
              ? currentScreenData.skipButtonText
              : t('onboarding.skip_button')}
            <IconArrowRight className="w-3 h-3" />
          </div>
        </Button>
      )}
    </AppFooter>
  );
};

export default OnBoardingFlowPageFooter;
